import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { SearchQuery } from "~/hooks/search/types";
import type { SeasonalEvent } from "~/integrations/firebase/collections/seasonal-events";
import { useSeasonalEventsQuery } from "~/components/seasonal-events/hooks/use-seasonal-events-query";
import { useSeasonalToday } from "~/components/seasonal-events/hooks/use-seasonal-today";
import { SeasonalEventsDropdown } from "./seasonal-events-dropdown";
import { getUpcomingEvents } from "./utils";

export const SeasonalEventsSearchArea = ({
  onEventSelect,
  children,
}: {
  onEventSelect: (event: SeasonalEvent) => void;
  children: ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const { events } = useSeasonalEventsQuery();
  const today = useSeasonalToday();
  const upcomingEvents = useMemo(
    () => getUpcomingEvents(events, today),
    [events, today],
  );

  const query = useWatch<SearchQuery>({ name: "query" });

  const showDropdown = hasFocusWithin && !query && upcomingEvents.length > 0;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !showDropdown) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && event.target instanceof HTMLElement) {
        event.target.blur();
      }
    };
    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDropdown]);

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions lint/a11y/noStaticElementInteractions: the wrapper only tracks focus within it; the controls inside are interactive
    <div
      ref={containerRef}
      className="flex w-full"
      onFocus={() => setHasFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHasFocusWithin(false);
        }
      }}
    >
      {children}
      {showDropdown && (
        <SeasonalEventsDropdown
          events={upcomingEvents}
          onEventSelect={(event) => {
            if (
              document.activeElement instanceof HTMLElement &&
              containerRef.current?.contains(document.activeElement)
            ) {
              document.activeElement.blur();
            }
            setHasFocusWithin(false);
            onEventSelect(event);
          }}
        />
      )}
    </div>
  );
};
